/**
 * useReveal — IntersectionObserver-based once-only reveal hook.
 *
 * Returns [ref, isRevealed]. Attach ref to any element; isRevealed flips to true
 * the first time the element crosses the threshold ratio in viewport, then the
 * observer is disconnected (once-only).
 *
 * SSR-safe (typeof window check). Reusable for B2 scroll-reveal site-wide.
 */

import { useEffect, useRef, useState } from 'react';

export function useReveal<T extends Element = HTMLDivElement>(
  threshold = 0.15,
): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const node = ref.current;
    if (!node) return;

    // Older browsers without IntersectionObserver — reveal immediately.
    if (typeof IntersectionObserver === 'undefined') {
      setRevealed(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setRevealed(true);
            observer.disconnect();
            break;
          }
        }
      },
      { threshold },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [threshold]);

  return [ref, revealed];
}
